import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Alamat',
      details: ['Jl. Poros Polewali - Majene', 'Matakali, Polewali Mandar, Sulawesi Barat'],
      color: 'blue'
    },
    {
      icon: Phone,
      title: 'Telepon',
      details: ['+62 8131-22222-30 (WhatsApp)'],
      color: 'red'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@ktconline.id', 'support@ktconline.id'],
      color: 'blue'
    },
    {
      icon: Clock,
      title: 'Jam Operasional',
      details: ['Senin - Jumat: 08:00 - 17:00', 'Sabtu: 09:00 - 14:00'],
      color: 'red'
    },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Pesan berhasil dikirim!');
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 3000);
    } catch (error) {
      toast.error('Gagal mengirim pesan. Silakan coba lagi.');
    }
  };

  return (
    <section 
      id="kontak" 
      className="py-24 md:py-32 bg-[#050505] relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-blue rounded-full filter blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-red rounded-full filter blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-widest uppercase text-brand-red mb-4 block">
            Hubungi Kami
          </span>
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Siap <span className="gradient-text-blue">Berlangganan</span>?
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Tim kami siap membantu Anda memilih paket terbaik. Hubungi kami sekarang!
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-outfit text-2xl font-semibold text-white mb-8">
              Informasi Kontak
            </h3>

            <div className="grid sm:grid-cols-2 gap-6">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="p-5 bg-[#121212] rounded-xl border border-white/5 hover:border-brand-blue/20 transition-colors"
                  >
                    <div className={`
                      icon-container mb-4
                      ${info.color === 'red' ? 'icon-container-red' : ''}
                    `}>
                      <Icon className={`w-5 h-5 ${info.color === 'red' ? 'text-brand-red' : 'text-brand-blue'}`} />
                    </div>
                    <h4 className="font-outfit font-semibold text-white mb-2">{info.title}</h4>
                    {info.details.map((detail, i) => (
                      <p key={i} className="text-sm text-gray-400">{detail}</p>
                    ))}
                  </motion.div>
                );
              })}
            </div>

            {/* Map Placeholder */}
            <div className="mt-8 rounded-xl overflow-hidden border border-white/10">
              <iframe
                src="https://maps.google.com/maps?q=Polewali%20Mandar%2C%20Sulawesi%20Barat&z=9&output=embed"
                width="100%"
                height="200"
                style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="KTC Location"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="glass rounded-2xl p-8">
              <h3 className="font-outfit text-2xl font-semibold text-white mb-6">
                Kirim Pesan
              </h3>

              {isSubmitted ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-white mb-2">
                    Pesan Terkirim!
                  </h4>
                  <p className="text-gray-400">
                    Tim kami akan menghubungi Anda segera.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Nama Lengkap
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Masukkan nama Anda"
                      required
                      className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 focus:border-brand-blue"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="email@example.com"
                        required
                        className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 focus:border-brand-blue"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        No. Telepon
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="08xx-xxxx-xxxx"
                        required
                        className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 focus:border-brand-blue"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Pesan
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tuliskan pesan atau pertanyaan Anda..."
                      rows={4}
                      required
                      className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 focus:border-brand-blue resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="btn-primary w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2"
                    data-testid="contact-submit-button"
                  >
                    <Send className="w-5 h-5" />
                    Kirim Pesan
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;




